import React, { lazy, Suspense } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";

import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { BarChart3, Zap, User } from "lucide-react";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { useActivityTracker } from "./hooks/useActivityTracker";
import { ImpersonationProvider } from "./contexts/ImpersonationContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { RoleGuard } from "./components/RoleGuard";
import { ImpersonationBanner } from "./components/ImpersonationBanner";
import { RoleBasedRedirect } from "./components/RoleBasedRedirect";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useRoutePreloader } from "./utils/routePreloader";

// Critical routes - keep as static imports
import Auth from "./pages/Auth";

// Lazy loaded pages - all other routes
const PerformanceSummary = lazy(() => import("./pages/PerformanceSummary"));
const PreLogin = lazy(() => import("./pages/PreLogin"));
const Overview = lazy(() => import("./pages/Overview"));
const Portfolio = lazy(() => import("./pages/Portfolio"));

const UsabilityReport = lazy(() => import("@/pages/UsabilityReport"));
const LatestReportCompliance = lazy(() => import("@/pages/LatestReportCompliance"));
const LatestReportProduct = lazy(() => import("@/pages/LatestReportProduct"));
const LatestReportAdMonetization = lazy(() => import("@/pages/LatestReportAdMonetization"));
const LatestReportUserAcquisition = lazy(() => import("@/pages/LatestReportUserAcquisition"));
const LatestReportStoreOptimization = lazy(() => import("@/pages/LatestReportStoreOptimization"));
const LatestReportSummary = lazy(() => import("@/pages/LatestReportSummary"));
const OperationalSummary = lazy(() => import("@/pages/OperationalSummary"));
const TaskManager = lazy(() => import("@/pages/TaskManager"));
const NegotiationManager = lazy(() => import("@/pages/NegotiationManager"));
const AppForm = lazy(() => import("@/pages/AppForm"));
const AppForm2 = lazy(() => import("@/pages/AppForm2"));
const TaskDetails = lazy(() => import("@/pages/TaskDetails"));
const AppDatabase = lazy(() => import("@/pages/AppDatabase"));
const AccountMapping = lazy(() => import("@/pages/AccountMapping"));
const ProvidersDatabase = lazy(() => import("@/pages/ProvidersDatabase"));
const ProviderMigration = lazy(() => import("@/pages/ProviderMigration"));
const CreativeDatabase = lazy(() => import("@/pages/CreativeDatabase"));
const AppDetails = lazy(() => import("@/pages/AppDetails"));
const ProviderDetails = lazy(() => import("@/pages/ProviderDetails"));
const UsabilityLab = lazy(() => import("./pages/UsabilityLab"));
const ComplianceLab = lazy(() => import("./pages/ComplianceLab"));
const ProductLab = lazy(() => import("./pages/ProductLab"));
const AdMonetizationLab = lazy(() => import("./pages/AdMonetizationLab"));
const UserAcquisitionLab = lazy(() => import("./pages/UserAcquisitionLab"));
const ASO = lazy(() => import("./pages/ASO"));
const TimelineDashboard = lazy(() => import("./pages/TimelineDashboard"));
const UserAssessmentVerification = lazy(() => import("./pages/UserAssessmentVerification"));
const Admin = lazy(() => import("./pages/Admin"));
const LabInitiatives = lazy(() => import("./pages/LabInitiatives"));

const PortfolioLatestReport = lazy(() => import("./pages/PortfolioLatestReport"));
const UsabilityPerformanceSummary = lazy(() => import("./pages/UsabilityPerformanceSummary"));
const UsabilityPortfolioLatestReport = lazy(() => import("./pages/UsabilityPortfolioLatestReport"));
const AppReport = lazy(() => import("./pages/AppReport"));
const TaskReport = lazy(() => import("./pages/TaskReport"));
const DealReport = lazy(() => import("./pages/DealReport"));
const MADealDetails = lazy(() => import("./pages/MADealDetails"));
const SubmissionReview = lazy(() => import("./pages/SubmissionReview"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Help = lazy(() => import("./pages/Help"));
const BigQueryTestPage = lazy(() => import("./pages/BigQueryTest"));
const FieldMappingManager = lazy(() => import("./pages/FieldMappingManager"));
const SdkFieldMapping = lazy(() => import("./pages/SdkFieldMapping"));
const SdkManager = lazy(() => import("./pages/SdkManager"));

const StrategicPortfolio = lazy(() => import("./pages/StrategicPortfolio"));
const UserActivityMonitor = lazy(() => import("./pages/UserActivityMonitor"));
const BitbucketOAuthCallback = lazy(() => import("./pages/BitbucketOAuthCallback"));
const WatchtowerIntegration = lazy(() => import("./pages/WatchtowerIntegration"));
const NotionArchive = lazy(() => import("./pages/NotionArchive"));
const NotionArchiveDetail = lazy(() => import("./pages/NotionArchiveDetail"));
const AppCodeAnalysis = lazy(() => import("./pages/AppCodeAnalysis"));
const CsvExtractor = lazy(() => import("./pages/CsvExtractor"));


// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

// Create query client with optimized configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,        // 5 minutes - data stays fresh
      gcTime: 30 * 60 * 1000,          // 30 minutes - cache lifetime
      retry: 3,                         // Retry failed requests 3 times
      retryDelay: (attemptIndex) => 
        Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
      refetchOnMount: 'always',         // Always refetch on mount
      refetchOnWindowFocus: true,       // Refetch when window regains focus
      refetchOnReconnect: true,         // Refetch when connection restored
    },
    mutations: {
      retry: 1,                         // Retry mutations once
      onError: (error: any) => {
        console.error('Mutation error:', error);
      },
    },
  },
});

function AppContent() {
  console.log('[PRODUCTION DEBUG] AppContent rendering...', {
    timestamp: new Date().toISOString(),
    pathname: window.location.pathname
  });
  
  const { user, signOut, loading } = useAuth();
  console.log('[PRODUCTION DEBUG] useAuth result:', {
    hasUser: !!user,
    loading,
    userId: user?.id
  });
  
  const location = useLocation();
  const isAppFormPage = location.pathname.startsWith('/app-form');
  const isEmbedMode = new URLSearchParams(location.search).get('embed') === '1';
  const isHelpPage = location.pathname.startsWith('/help');
  
  console.log('[PRODUCTION DEBUG] Page detection:', {
    isAppFormPage,
    isEmbedMode,
    isHelpPage,
    pathname: location.pathname
  });
  
  // Activity tracker - properly called as a hook
  useActivityTracker();

  // Intelligent route preloading - preload frequently accessed routes
  const userRole = user?.user_metadata?.role || 'user';
  useRoutePreloader(user, userRole);

  const handleSignOut = async () => {
    console.log('[PRODUCTION DEBUG] Sign out initiated');
    await signOut();
  };

  // Allow app-form page and embed mode to load even during auth loading
  if (loading && !isAppFormPage && !isEmbedMode) {
    console.log('[PRODUCTION DEBUG] Showing loading spinner');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  console.log('[PRODUCTION DEBUG] Rendering main app content');

  return (
    <SidebarProvider>
        <ImpersonationBanner />
        <div className="min-h-screen flex w-full bg-background">
          {user && !isAppFormPage && !isEmbedMode && !isHelpPage && <AppSidebar />}
          <div className="flex-1 flex flex-col">
            <main className="flex-1">
            <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={user ? <RoleBasedRedirect /> : <Auth />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/role-redirect" element={
                <ProtectedRoute>
                  <RoleBasedRedirect />
                </ProtectedRoute>
              } />
            <Route path="/latest-report" element={
              <ProtectedRoute>
                <RoleGuard allowedRoles={['admin', 'department_manager', 'procurement_manager']}>
                  <LatestReportSummary />
                </RoleGuard>
              </ProtectedRoute>
            } />
            <Route path="/latest-report-usability" element={
              <ProtectedRoute>
                <RoleGuard allowedRoles={['admin', 'department_manager', 'procurement_manager']}>
                  <UsabilityReport />
                </RoleGuard>
              </ProtectedRoute>
            } />
            <Route path="/latest-report-compliance" element={
              <ProtectedRoute>
                <RoleGuard allowedRoles={['admin', 'department_manager']} requiredLab="compliance">
                  <LatestReportCompliance />
                </RoleGuard>
              </ProtectedRoute>
            } />
            <Route path="/latest-report-product" element={
              <ProtectedRoute>
                <RoleGuard allowedRoles={['admin', 'department_manager']} requiredLab="product">
                  <LatestReportProduct />
                </RoleGuard>
              </ProtectedRoute>
            } />
            <Route path="/latest-report-ad-monetization" element={
              <ProtectedRoute>
                <RoleGuard allowedRoles={['admin', 'department_manager']} requiredLab="ad-monetization">
                  <LatestReportAdMonetization />
                </RoleGuard>
              </ProtectedRoute>
            } />
            <Route path="/latest-report-user-acquisition" element={
              <ProtectedRoute>
                <RoleGuard allowedRoles={['admin', 'department_manager']} requiredLab="user-acquisition">
                  <LatestReportUserAcquisition />
                </RoleGuard>
              </ProtectedRoute>
            } />
            <Route path="/latest-report-store-optimization" element={
              <ProtectedRoute>
                <RoleGuard allowedRoles={['admin', 'department_manager']} requiredLab="aso">
                  <LatestReportStoreOptimization />
                </RoleGuard>
              </ProtectedRoute>
            } />
            <Route path="/operational-summary" element={
              <ProtectedRoute>
                <RoleGuard allowedRoles={['admin']} requiredPage="/operational-summary">
                  <OperationalSummary />
                </RoleGuard>
              </ProtectedRoute>
            } />
            <Route path="/task-manager" element={
              <ProtectedRoute>
                <RoleGuard requiredPage="/task-manager">
                  <TaskManager />
                </RoleGuard>
              </ProtectedRoute>
            } />
            <Route path="/negotiation-manager" element={
              <ProtectedRoute>
                <RoleGuard allowedRoles={['admin', 'ma_manager']} requiredPage="/negotiation-manager">
                  <NegotiationManager />
                </RoleGuard>
              </ProtectedRoute>
            } />
            {/* Public route - no authentication required */}
            <Route path="/app-form" element={<AppForm />} />
            <Route path="/app-form-2" element={<AppForm2 />} />
            <Route path="/bitbucket-oauth-callback" element={<BitbucketOAuthCallback />} />
            <Route path="/task-details/:ticketId" element={
              <ProtectedRoute>
                <RoleGuard allowedRoles={['admin','department_manager','creative_manager','procurement_manager','provider_manager','ma_manager','qa_manager','tech_apps','tech_infrastructure']}>
                  <TaskDetails />
                </RoleGuard>
              </ProtectedRoute>
            } />
            <Route path="/ma-deal-details/:dealId" element={
              <ProtectedRoute>
                <MADealDetails />
              </ProtectedRoute>
            } />
            <Route path="/submission-review/:submissionId" element={
              <ProtectedRoute>
                <SubmissionReview />
              </ProtectedRoute>
            } />
            <Route path="/csv-extractor" element={
              <ProtectedRoute>
                <CsvExtractor />
              </ProtectedRoute>
            } />
            <Route path="/task-report/:ticketId" element={
              <ProtectedRoute>
                <RoleGuard allowedRoles={['procurement_manager']}>
                  <TaskReport />
                </RoleGuard>
              </ProtectedRoute>
            } />
            <Route path="/deal-report/:dealId" element={
              <ProtectedRoute>
                <RoleGuard allowedRoles={['admin', 'ma_manager']}>
                  <DealReport />
                </RoleGuard>
              </ProtectedRoute>
            } />
            <Route path="/app-database" element={
              <ProtectedRoute>
                <RoleGuard allowedRoles={['admin', 'department_manager', 'procurement_manager', 'ma_manager', 'qa_manager', 'creative_manager']} requiredPage="/app-database">
                  <AppDatabase />
                </RoleGuard>
              </ProtectedRoute>
            } />
            <Route path="/app-code-analysis" element={
              <ProtectedRoute>
                <RoleGuard allowedRoles={['admin']}>
                  <AppCodeAnalysis />
                </RoleGuard>
              </ProtectedRoute>
            } />
            <Route path="/account-mapping" element={
              <ProtectedRoute>
                <RoleGuard allowedRoles={['admin', 'department_manager', 'procurement_manager', 'ma_manager', 'qa_manager']}>
                  <AccountMapping />
                </RoleGuard>
              </ProtectedRoute>
            } />
            <Route path="/providers-database" element={
              <ProtectedRoute>
                <RoleGuard allowedRoles={['admin', 'procurement_manager']} requiredPage="/providers-database">
                  <ProvidersDatabase />
                </RoleGuard>
              </ProtectedRoute>
            } />
            <Route path="/provider-migration" element={
              <ProtectedRoute>
                <RoleGuard allowedRoles={['admin']}>
                  <ProviderMigration />
                </RoleGuard>
              </ProtectedRoute>
            } />
            <Route path="/creative-database" element={
              <ProtectedRoute>
                <RoleGuard allowedRoles={['admin', 'procurement_manager', 'creative_manager']} requiredPage="/creative-database">
                  <CreativeDatabase />
                </RoleGuard>
              </ProtectedRoute>
            } />
            <Route path="/creative-database/:folderId" element={
              <ProtectedRoute>
                <RoleGuard allowedRoles={['admin', 'procurement_manager', 'creative_manager']}>
                  <CreativeDatabase />
                </RoleGuard>
              </ProtectedRoute>
            } />
            <Route path="/app-details/:id" element={
              <ProtectedRoute>
                <RoleGuard allowedRoles={['admin', 'department_manager', 'procurement_manager', 'ma_manager', 'qa_manager']}>
                  <AppDetails />
                </RoleGuard>
              </ProtectedRoute>
            } />
            <Route path="/app-report/:appId" element={
              <ProtectedRoute>
                <RoleGuard allowedRoles={['procurement_manager']}>
                  <AppReport />
                </RoleGuard>
              </ProtectedRoute>
            } />
            <Route path="/provider-details/:providerId" element={
              <ProtectedRoute>
                <RoleGuard allowedRoles={['admin', 'procurement_manager']}>
                  <ProviderDetails />
                </RoleGuard>
              </ProtectedRoute>
            } />
              <Route path="/performance-summary" element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin', 'department_manager', 'creative_manager']} requiredPage="/performance-summary">
                    <PerformanceSummary />
                  </RoleGuard>
                </ProtectedRoute>
              } />
              <Route path="/portfolio-latest-report" element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin', 'department_manager', 'creative_manager']}>
                    <PortfolioLatestReport />
                  </RoleGuard>
                </ProtectedRoute>
              } />
              <Route path="/usability-performance-summary" element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin', 'department_manager', 'procurement_manager']}>
                    <UsabilityPerformanceSummary />
                  </RoleGuard>
                </ProtectedRoute>
              } />
              <Route path="/usability-portfolio-latest-report" element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin', 'department_manager']} requiredLab="usability">
                    <UsabilityPortfolioLatestReport />
                  </RoleGuard>
                </ProtectedRoute>
              } />
              <Route path="/overview" element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin', 'department_manager', 'procurement_manager']} requiredPage="/overview">
                    <Overview />
                  </RoleGuard>
                </ProtectedRoute>
              } />
              <Route path="/timeline-dashboard" element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin']} requiredPage="/timeline-dashboard">
                    <TimelineDashboard />
                  </RoleGuard>
                </ProtectedRoute>
              } />
              <Route path="/usability" element={
                <ProtectedRoute>
                  <RoleGuard requiredPage="/usability">
                    <UsabilityLab />
                  </RoleGuard>
                </ProtectedRoute>
              } />
              <Route path="/compliance" element={
                <ProtectedRoute>
                  <RoleGuard requiredPage="/compliance">
                    <ComplianceLab />
                  </RoleGuard>
                </ProtectedRoute>
              } />
              <Route path="/product" element={
                <ProtectedRoute>
                  <RoleGuard requiredPage="/product">
                    <ProductLab />
                  </RoleGuard>
                </ProtectedRoute>
              } />
              <Route path="/ad-monetization" element={
                <ProtectedRoute>
                  <RoleGuard requiredPage="/ad-monetization">
                    <AdMonetizationLab />
                  </RoleGuard>
                </ProtectedRoute>
              } />
              <Route path="/user-acquisition" element={
                <ProtectedRoute>
                  <RoleGuard requiredPage="/user-acquisition">
                    <UserAcquisitionLab />
                  </RoleGuard>
                </ProtectedRoute>
              } />
              <Route path="/aso" element={
                <ProtectedRoute>
                  <RoleGuard requiredPage="/aso">
                    <ASO />
                  </RoleGuard>
                </ProtectedRoute>
              } />
              <Route path="/admin" element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin']} requiredPage="/admin">
                    <Admin />
                  </RoleGuard>
                </ProtectedRoute>
              } />
              <Route path="/user-assessment-verification" element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin']}>
                    <UserAssessmentVerification />
                  </RoleGuard>
                </ProtectedRoute>
              } />
              <Route path="/lab-initiatives" element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin']}>
                    <LabInitiatives />
                  </RoleGuard>
                </ProtectedRoute>
              } />
              <Route path="/field-mapping" element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin']}>
                    <FieldMappingManager />
                  </RoleGuard>
                </ProtectedRoute>
              } />
              <Route path="/sdk-field-mapping" element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin']}>
                    <SdkFieldMapping />
                  </RoleGuard>
                </ProtectedRoute>
              } />
              <Route path="/sdk-manager" element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin']}>
                    <SdkManager />
                  </RoleGuard>
                </ProtectedRoute>
              } />
              <Route path="/bigquery-test" element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin']}>
                    <BigQueryTestPage />
                  </RoleGuard>
                </ProtectedRoute>
              } />
              <Route path="/strategic-portfolio" element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin', 'procurement_manager', 'ma_manager']}>
                    <StrategicPortfolio />
                  </RoleGuard>
                </ProtectedRoute>
              } />
              <Route path="/user-activity-monitor" element={
                <ProtectedRoute>
                  <RoleGuard allowedRoles={['admin']}>
                    <UserActivityMonitor />
                  </RoleGuard>
                </ProtectedRoute>
              } />
            <Route path="/watchtower-integration" element={
              <ProtectedRoute>
                <RoleGuard allowedRoles={['admin']}>
                  <WatchtowerIntegration />
                </RoleGuard>
              </ProtectedRoute>
            } />
            <Route path="/notion-archive" element={
              <ProtectedRoute>
                <RoleGuard allowedRoles={['admin']}>
                  <NotionArchive />
                </RoleGuard>
              </ProtectedRoute>
            } />
            <Route path="/notion-archive/:migrationId" element={
              <ProtectedRoute>
                <RoleGuard allowedRoles={['admin']}>
                  <NotionArchiveDetail />
                </RoleGuard>
              </ProtectedRoute>
            } />
              <Route path="/help" element={
                <ProtectedRoute>
                  <Help />
                </ProtectedRoute>
              } />
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ReactQueryDevtools initialIsOpen={false} />
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <ImpersonationProvider>
            <AppContent />
          </ImpersonationProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;